import { useEffect, useCallback, useState } from 'react';
import { RouteComponentProps, useNavigate } from '@reach/router';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { Form, FormState } from 'informed';
import { useDispatch, useSelector } from 'react-redux';
import { axisPaths } from './AxisConstants';
import { useEnsureCalefyFormPresent } from '../../hooks/useEnsureCalefyFormPresent';
import { useAxisQuoteSubmission } from '../../hooks/useAxisQuoteSubmission';
import { withFormStyles, WithFormStyles } from '../../util/withFormStyles';
import RenderedQuestionInstance from '../common/QuoteComponents/RenderedQuestionInstance';
import { AxisFormLoadingIndicator } from './components/AxisFormLoadingIndicator';
import { AxisQuoteSubmissionProgress } from './components/AxisQuoteSubmissionProgress';
import { setAxisStructuredData } from '../../store/axisStore';
import QuoteWizardState, { updateErrors } from '../../store/QuoteWizardState';
import { resetAxisState } from '../../store/axisStore';
import { CompleteQuoteSubmissionError } from '../QuoteWizard/ErrorHandling';
import { errorify } from '../../util';
import {
  AXIS_CLAIMS_INQUIRY_STRUCTURED_DATA,
  AXIS_CURRENT_CLIENT_STRUCTURED_DATA,
  AXIS_OTHER_INQUIRY_STRUCTURED_DATA,
  AXIS_PAYMENTS_STRUCTURED_DATA,
  AXIS_PROMOTIONAL_STRUCTURED_DATA,
  AxisTopLevelStructuredData,
} from './axisStructuredDataConstants';
import { submitAxisInquiryQuote } from './submitAxisInquiryQuote';
import { StoreState } from '../../store';
import { AxisLogo } from './components/AxisLogo';

const CURRENT_CLIENT_INTRODUCTORY_TEXT = `NOTE: For fastest service, please contact your broker directly before using this form.
Please include a detailed message below letting our account team know what your inquiry is about (Updates to your business, coverage changes, certificates of insurance, invoices, cancellations, or renewals).`;

const CLAIMS_INQUIRY_INTRODUCTORY_TEXT =
  'NOTE: For fastest service, please contact your insurance company directly. Alternatively, use our 24-hour phone line at 1-800-387-5677 or reach out to your Axis broker directly.';

export const OtherInquiryPage = (_props: RouteComponentProps) => {
  return (
    <BaseBasicInformationComponent
      heading='Other Inquiry'
      formInternalName='axis_general_inquiry_other'
      structuredData={AXIS_OTHER_INQUIRY_STRUCTURED_DATA}
    />
  );
};

export const PromotionalPage = (_props: RouteComponentProps) => {
  return (
    <BaseBasicInformationComponent
      heading='Marketing or Promotional'
      formInternalName='axis_general_inquiry_marketing_or_promotional'
      structuredData={AXIS_PROMOTIONAL_STRUCTURED_DATA}
    />
  );
};

export const PaymentsPage = (_props: RouteComponentProps) => {
  return (
    <BaseBasicInformationComponent
      heading='Finance or Payment'
      formInternalName='axis_general_inquiry_finance_or_payment'
      structuredData={AXIS_PAYMENTS_STRUCTURED_DATA}
    />
  );
};

export const ClaimsInquiryPage = (_props: RouteComponentProps) => {
  return (
    <BaseBasicInformationComponent
      heading='Claims Inquiry'
      introductoryText={CLAIMS_INQUIRY_INTRODUCTORY_TEXT}
      formInternalName='axis_general_inquiry_claims_inquiry'
      structuredData={AXIS_CLAIMS_INQUIRY_STRUCTURED_DATA}
    />
  );
};

export const CurrentClientPage = (_props: RouteComponentProps) => {
  return (
    <BaseBasicInformationComponent
      heading='Current or Former Client'
      introductoryText={CURRENT_CLIENT_INTRODUCTORY_TEXT}
      formInternalName='axis_general_inquiry_current_client'
      structuredData={AXIS_CURRENT_CLIENT_STRUCTURED_DATA}
    />
  );
};

interface BaseBasicInformationComponentProps extends WithFormStyles {
  formInternalName: string;
  heading: string;
  introductoryText?: string;
  structuredData: AxisTopLevelStructuredData;
}

export const BaseBasicInformationComponent = withFormStyles()(
  ({
    formInternalName,
    heading,
    introductoryText,
    structuredData,
    classes,
  }: BaseBasicInformationComponentProps) => {
    const { form, loading } = useEnsureCalefyFormPresent({
      internalName: formInternalName,
    });
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [submissionStarted, setSubmissionStarted] = useState(false);
    const submissionError = useSelector(
      (state: StoreState) => state.quoteWizard.errors.completeQuoteSubmission,
    );
    const currentQuoteUUID = useSelector(
      (state: StoreState) => state.quoteWizard.currentQuoteUUID,
    );

    const handleSubmissionComplete = useCallback(() => {
      dispatch(QuoteWizardState.actions.resetSelections(['questionsPerPage']));
      dispatch(resetAxisState());
      navigate(axisPaths.thanks.url);
    }, [dispatch, navigate]);

    const { taskPool } = useAxisQuoteSubmission({
      active: submissionStarted,
      onComplete: handleSubmissionComplete,
    });

    useEffect(() => {
      dispatch(
        setAxisStructuredData({
          ...structuredData,
          industry: '',
          subIndustry: '',
        }),
      );
    }, [dispatch, structuredData]);

    useEffect(() => {
      const taskPoolData = taskPool.data?.getTaskPool;
      const error = taskPool.error || taskPoolData?.error;

      if (!submissionStarted || !error) {
        return;
      }

      setSubmissionStarted(false);
      dispatch(
        updateErrors({
          completeQuoteSubmission: errorify(error),
        }),
      );
    }, [
      dispatch,
      submissionStarted,
      taskPool.data?.getTaskPool,
      taskPool.error,
    ]);

    useEffect(() => {
      if (submissionStarted && submissionError) {
        setSubmissionStarted(false);
      }
    }, [submissionStarted, submissionError]);

    const handleSubmit = useCallback(
      (formState: FormState) => {
        if (!form || submissionStarted) {
          return;
        }

        setSubmissionStarted(true);
        submitAxisInquiryQuote(dispatch, form, formState, currentQuoteUUID);
      },
      [dispatch, form, submissionStarted, currentQuoteUUID],
    );

    const submissionInProgress = submissionStarted;

    return (
      <Outer>
        <Card>
          <AxisLogo placement='corner' />
          <PageHeading>{heading}</PageHeading>
          {introductoryText ? (
            <IntroductoryText>{introductoryText}</IntroductoryText>
          ) : null}
          {loading ? <AxisFormLoadingIndicator /> : null}
          {submissionInProgress ? (
            <AxisQuoteSubmissionProgress
              taskPool={taskPool.data?.getTaskPool ?? null}
            />
          ) : form ? (
            <FormSection>
              <Form
                autoComplete='off'
                allowEmptyStrings
                className={classes.form}
                onSubmit={handleSubmit}
              >
                {(form.questionInstances || []).map((questionInstance) => (
                  <RenderedQuestionInstance
                    key={questionInstance.id}
                    questionInstance={questionInstance}
                    classes={classes}
                  />
                ))}
                <SubmitButtonContainer>
                  <SecondaryButton
                    type='button'
                    onClick={() => navigate(axisPaths.welcome.url)}
                  >
                    Back
                  </SecondaryButton>
                  <SubmitButton type='submit'>Submit</SubmitButton>
                </SubmitButtonContainer>
              </Form>
            </FormSection>
          ) : null}
          <CompleteQuoteSubmissionError />
        </Card>
      </Outer>
    );
  },
);

export const DemoBasicInformationPage = (_props: RouteComponentProps) => {
  return (
    <BaseBasicInformationComponent
      formInternalName='axis_general_inquiry_current_client'
      heading='Test'
      introductoryText='Here is some text'
      structuredData={AXIS_CURRENT_CLIENT_STRUCTURED_DATA}
    />
  );
};

const Outer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '50vh',
  padding: theme.spacing(4),
}));

const Card = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: 820,
  padding: theme.spacing(9, 5, 5),
  textAlign: 'center',
}));

const PageHeading = styled(Typography)(({ theme }) => ({
  ...theme.typography.h4,
  marginBottom: theme.spacing(2),
}));

const IntroductoryText = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
  whiteSpace: 'pre-line',
}));

const FormSection = styled(Box)({
  textAlign: 'left',
});

const SubmitButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'center',
  marginTop: theme.spacing(3),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  ...theme.typography.button,
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
  textTransform: 'none',
  boxShadow: theme.shadows[2],
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: theme.shadows[4],
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  ...theme.typography.button,
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'transparent',
  padding: `${theme.spacing(0.75)} ${theme.spacing(2.5)}`,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
}));
